import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, BookOpen, Brain, Users } from 'lucide-react';
import { professionalData } from '../professionalData';

const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section 
        className="section" 
        style={{ 
          minHeight: '85vh',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'var(--background)'
        }}
        data-testid="hero-section"
      >
        <div className="container">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Text Content */}
            <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
              <p className="uppercase-small" style={{ marginBottom: '1rem' }}>
                Tri-Continental Academic Training
              </p>
              <h1 style={{ 
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
                lineHeight: 1.1,
                marginBottom: '1.5rem',
                color: 'var(--primary)'
              }}>
                Translating the Complexity of the{' '}
                <span style={{ 
                  fontStyle: 'italic',
                  color: 'var(--accent)'
                }}>
                  Developing Brain
                </span>{' '}
                into Actionable Daily Progress.
              </h1>
              <p style={{ 
                fontSize: '1.5rem',
                fontWeight: 600,
                lineHeight: 1.4,
                color: 'var(--primary)',
                marginBottom: '1.5rem',
                fontFamily: 'var(--font-body)'
              }}>
                Objective Diagnostics. Neuro-Affirming Design. Transforming the High-Stakes of Child Development.
              </p>
              <p style={{ 
                fontSize: '1.25rem',
                lineHeight: 1.8,
                color: 'var(--text-muted)',
                marginBottom: '2rem'
              }}>
                In high-stakes moments—a child&apos;s first words after months of tailored intervention, or a family&apos;s chaos turning into a manageable routine—I have seen the profound impact of evidence-based precision. My work is dedicated to turning complex psychological and neurodevelopmental challenges into actionable progress for everyone.
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Link 
                  to="/about" 
                  className="btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  data-testid="learn-more-button"
                >
                  Learn More About My Work
                  <ArrowRight size={20} />
                </Link>
                <a 
                  href={professionalData.clinic.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  data-testid="visit-clinic-button"
                >
                  Visit Modern Minds Clinic
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Pedigree Section */}
      <section 
        className="section-alt" 
        style={{ backgroundColor: 'var(--surface)' }}
        data-testid="education-section"
      >
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <p className="uppercase-small" style={{ marginBottom: '1rem' }}>
              Education
            </p>
            <h2>Global Foundations</h2>
            <p style={{ 
              fontSize: '1.125rem',
              color: 'var(--text-muted)',
              maxWidth: '700px',
              margin: '1rem auto 0'
            }}>
              Tri-continental academic pedigree across three leading institutions
            </p>
          </div>

          <div className="grid-3">
            {professionalData.education.map((edu, index) => (
              <div 
                key={index}
                className="glass-card"
                style={{
                  textAlign: 'center',
                  position: 'relative'
                }}
                data-testid={`education-card-${index}`}
              >
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '3rem',
                  color: 'var(--accent)',
                  opacity: 0.1,
                  fontWeight: 700
                }}>
                  {index + 1}
                </div>
                <p className="uppercase-small" style={{ marginBottom: '0.75rem', color: 'var(--accent)' }}>
                  {edu.location}
                </p>
                <h3 style={{ 
                  fontSize: '1.25rem',
                  marginBottom: '0.5rem',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600
                }}>
                  {edu.institution}
                </h3>
                <p style={{ 
                  fontWeight: 500,
                  marginBottom: '0.5rem',
                  color: 'var(--primary)'
                }}>
                  {edu.degree}
                </p>
                <p style={{ 
                  fontSize: '0.875rem',
                  color: 'var(--text-muted)',
                  marginBottom: '0.5rem'
                }}>
                  {edu.description}
                </p>
                <p style={{ 
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'var(--text-muted)'
                }}>
                  {edu.year}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Pillars Preview */}
      <section className="section" data-testid="pillars-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <p className="uppercase-small" style={{ marginBottom: '1rem' }}>
              Clinical Expertise
            </p>
            <h2>How I Engineer Growth</h2>
          </div>

          <div className="grid-3">
            {professionalData.pillars.map((pillar, index) => {
              const icons = [Brain, Users, BookOpen];
              const Icon = icons[index];
              return (
                <div 
                  key={index}
                  className="glass-card"
                  style={{
                    borderLeft: '3px solid var(--accent)'
                  }}
                  data-testid={`pillar-${index}`}
                >
                  <Icon size={40} color="var(--accent)" style={{ marginBottom: '1rem' }} />
                  <h3 style={{ 
                    fontSize: '1.5rem',
                    marginBottom: '1rem',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 600,
                    color: 'var(--primary)'
                  }}>
                    {pillar.title}
                  </h3>
                  <p style={{ 
                    color: 'var(--text-muted)',
                    lineHeight: 1.8
                  }}>
                    {pillar.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link 
              to="/services" 
              className="btn-primary"
              data-testid="view-services-button"
            >
              View Clinical Services
            </Link>
          </div>
        </div>
      </section>

      {/* Modern Minds Bridge */}
      <section 
        className="section" 
        data-testid="modern-minds-section"
      >
        <div className="container">
          <div className="glass-card-strong" style={{ 
            maxWidth: '1000px', 
            margin: '0 auto',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <p className="uppercase-small" style={{ 
              marginBottom: '1rem'
            }}>
              Clinical Practice
            </p>
            <h2 style={{ marginBottom: '1rem' }}>
              {professionalData.clinic.name}
            </h2>
            <p style={{ 
              fontSize: '1.125rem',
              marginBottom: '1rem',
              color: 'var(--text-muted)'
            }}>
              {professionalData.clinic.subtitle}
            </p>
            <p style={{ 
              fontSize: '1rem',
              lineHeight: 1.8,
              marginBottom: '2rem',
              color: 'var(--text-muted)',
              maxWidth: '700px',
              margin: '0 auto 2rem'
            }}>
              {professionalData.clinic.description}
            </p>
            <a 
              href={professionalData.clinic.website}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                textDecoration: 'none',
                fontWeight: 500,
                border: 'none'
              }}
              data-testid="modern-minds-cta"
            >
              Visit Modern Minds Clinic
              <ExternalLink size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ textAlign: 'center' }} data-testid="cta-section">
        <div className="container">
          <h2 style={{ marginBottom: '1.5rem' }}>
            Ready to Begin?
          </h2>
          <p style={{ 
            fontSize: '1.125rem',
            color: 'var(--text-muted)',
            marginBottom: '2rem',
            maxWidth: '600px',
            margin: '0 auto 2rem'
          }}>
            Explore my research, learn about my clinical approach, or get in touch for collaboration inquiries.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/research" className="btn-primary" data-testid="research-cta">
              View Research & Innovation
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="contact-cta">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
